// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { useAcceptBurnRequestContext } from '@hooks/mutations/registry/burn/context/useAcceptBurnRequestContext';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import {
  BurnRequest,
  BurnRequest_Accept,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Burn';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_ACC_BURN, SUC_ACC_BURN } from '../../../../utils/strings';

interface Args {
  burnRequestId: ContractId<BurnRequest>;
}

export const useAcceptBurn2Step = () => {
  const getAcceptRequestBurnContext = useAcceptBurnRequestContext();
  const { submit: acceptBurnRequest } = useExerciseSubmission(
    BurnRequest.BurnRequest_Accept,
    SUC_ACC_BURN,
    ERR_ACC_BURN,
  );

  return async (args: Args) => {
    const { burnRequestId } = args;

    const acceptRequestBurnContext = await getAcceptRequestBurnContext.mutateAsync({
      burnRequestId,
    });

    const payload: BurnRequest_Accept = {
      extraArgs: {
        context: acceptRequestBurnContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };

    const disclosedContracts =
      acceptRequestBurnContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await acceptBurnRequest({
      cid: burnRequestId,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
};
